import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageLoader from '../../components/loader';
import Table from '../../components/table';
import { invitationsService } from '../../lib/api/invitations';
import { InviteStatusEnum, InviteType, InviteTypeEnum } from '../../types/invitations';
import { getCandidateInviteTypeText, getInviteStatusText } from '../../lib/utils/dictionary';
import CreateInvite from './modals/create-candidate';
import { toast } from 'react-toastify';
import { Messages } from '../../lib/utils/messages';
import Swal from 'sweetalert2';
import { Message, SweetAlertTitle, WarningIcon } from '../../lib/constants/messages';
import { Helmet } from 'react-helmet';
import CandidateExcelBulkUpload from './candidate-bulk-upload';
import { getonboardAndExitReviewSettings } from '../../redux/onboardAndExitReview';
import { getCOEonboardReviewSettings } from '../../redux/COEonboardAReview';
import { useConditionalEffects } from '../../hooks/force-signout';
import useUser, { useCOEReviewFlow, useExitReviewFlow } from '../../hooks/useSelector';
import { useAppDispatch } from '../../redux/reduxhooks';

export const showWarning = (hasUser: any, setShowVerificationWarning?: any) => {
    if ((hasUser && hasUser.suspendedStatus === 2) || (hasUser && hasUser.suspendedStatus === 5)) {
        Swal.fire({
            title: SweetAlertTitle.Warning,
            text: Message.WaitingForApproval,
            icon: WarningIcon,
        });
    } else if (hasUser && hasUser.suspendedStatus === 3) {
        Swal.fire({
            title: SweetAlertTitle.Warning,
            text: Message.WaitingForApproval,
            icon: WarningIcon,
        });
    } else if (hasUser && hasUser.suspendedStatus === 4) {
        Swal.fire({
            title: SweetAlertTitle.Warning,
            text: Message.RejectionMessage,
            icon: WarningIcon,
        });
    } else {
        setShowVerificationWarning?.(true);
        return true;
    }
};

const CandidateInvitations = () => {
    const hasUser = useUser();
    const [data, setData] = useState<InviteType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isExitReviewEnabled, setIsExitReviewEnabled] = useState<boolean | null>(null);
    const [loadingResendStatus, setLoadingResendStatus] = useState<'loading' | 'success' | 'error' | null>(null);
    const [activeResendId, setActiveResendId] = useState('');
    const [isCOEReviewEnabled, setIsCOEReviewEnabled] = useState<boolean | null>(null);
    const signOutServiceComponent = useConditionalEffects();

    const [showVerificationWarning, setShowVerificationWarning] = useState(
        (hasUser && hasUser.suspendedStatus === 0) || (hasUser && hasUser.suspendedStatus === 6) ? true : false
    );

    const [invitationHeaderText, setInvitationHeaderText] = useState('Candidate invitations');

    const dispatch = useAppDispatch();
    const onboardAndExitWorkFlowSettings = useExitReviewFlow();
    const COEonboardWorkFlowSettings = useCOEReviewFlow();
    const [showBulkUploadPage, setShowBulkUploadPage] = useState(false);
    const [isExistingCandidate, setIsExistingCandidate] = useState(true);

    const atIndex = hasUser && hasUser?.companyEmail?.indexOf('@');
    const employerCompanyDomainValue =
        atIndex !== -1 && atIndex !== undefined ? hasUser && hasUser?.companyEmail?.substring(atIndex + 1) : null;

    const columns = useMemo(
        () => [
            {
                dataIndex: 'fullName',
                title: 'Candidate name',
                render: (item: any) => (
                    <div className="text-nowrap text-ellipsis text-capitalize" title={item.fullName}>
                        {item.fullName}
                    </div>
                ),
            },
            {
                dataIndex: 'email',
                title: 'Email',
                render: (item: any) => (
                    <div className="text-nowrap text-ellipsis" title={item.email}>
                        {item.email}
                    </div>
                ),
            },
            {
                dataIndex: 'invitedDate',
                title: 'Date invited',
                inputFilter: (value: any) => {
                    return new Date(value).toLocaleString('en-in', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    });
                },
                render: (item: any) =>
                    new Date(item.invitedDate).toLocaleString('en-in', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    }),
            },
            {
                dataIndex: 'inviteStatus',
                title: 'Status',
                render: (item: any) => (
                    <span style={{ color: `${getStatusColor(item.inviteStatus)}` }}>
                        {item.isOtherJoined
                            ? getInviteStatusText(InviteStatusEnum.elsewhere)
                            : getInviteStatusText(item.inviteStatus)}
                    </span>
                ),
            },
            {
                dataIndex: 'inviteType',
                title: 'Invite type',
                // inputFilter: (value: any) => {
                //     return value.toLocaleString();
                // },
                render: (item: any) => (
                    <span style={{ color: `${getInviteTypeColor(item.inviteType)}` }}>
                        {getCandidateInviteTypeText(item.inviteType)}
                    </span>
                ),
            },
            {
                dataIndex: '',
                title: 'Actions',
                width: '120px',
                render: (item: any) =>
                    item.inviteStatus === InviteStatusEnum.invited && (
                        <div className="d-flex justify-content-center">
                            <button
                                type="button"
                                onClick={() => {
                                    setActiveResendId(item.id);
                                    signOutServiceComponent();
                                }}
                                className="btn-link"
                            >
                                {loadingResendStatus === 'error' && activeResendId === item.id && (
                                    <span className="google-icon">
                                        <i className="bi bi-x-circle text-danger" />
                                    </span>
                                )}
                                {loadingResendStatus === 'loading' && activeResendId === item.id && (
                                    <div className="new-spinner p-fixed">
                                        <div>
                                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                                        </div>
                                        <p className="fs-14 custom-loading-text">Loading</p>
                                    </div>
                                )}
                                Resend
                            </button>
                        </div>
                    ),
            },
        ],
        [loadingResendStatus, activeResendId]
    );

    const getList = useCallback(async () => {
        try {
            !loading && setLoading(true);
            const {
                resultObject: { items },
            } = await invitationsService.getList();
            const mapItems = items.map((item) => ({
                ...item,
                fullName: `${item.firstName} ${item.middleName && item.middleName + ' '}${item.lastName}`,
            }));
            setData(mapItems);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }, [loading]);

    useEffect(() => {
        if (!activeResendId) return;
        (async function () {
            try {
                setLoadingResendStatus('loading');
                await invitationsService.resend(activeResendId);
                toast.success(Messages.InvitationResend);
                setLoadingResendStatus('success');
                setActiveResendId('');
            } catch (err) {
                setLoadingResendStatus('error');
            }
        })();
    }, [activeResendId]);

    useEffect(() => {
        (async function () {
            await getList();
        })();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (onboardAndExitWorkFlowSettings.settings == null) {
                    dispatch(getonboardAndExitReviewSettings(hasUser && hasUser.isOffboardingReviewsEnabled));
                } else {
                    setIsExitReviewEnabled(onboardAndExitWorkFlowSettings.settings);
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, [dispatch, hasUser && hasUser.isOffboardingReviewsEnabled, onboardAndExitWorkFlowSettings.settings]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (COEonboardWorkFlowSettings.settings == null) {
                    dispatch(getCOEonboardReviewSettings(hasUser && hasUser.isCOEEnabled));
                } else {
                    setIsCOEReviewEnabled(COEonboardWorkFlowSettings.settings);
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, [dispatch, hasUser && hasUser.isCOEEnabled, COEonboardWorkFlowSettings.settings]);

    useEffect(() => {
        if (!showBulkUploadPage) {
            (async function () {
                await getList();
            })();
        }
    }, [showBulkUploadPage]);

    useEffect(() => {
        if (!showBulkUploadPage) {
            setInvitationHeaderText('Candidate invitations');
        }
    }, [showBulkUploadPage]);

    return (
        <>
            <Helmet>
                <title>OfferX - Invitations | Say No To Ghosting Interviews</title>
                <meta
                    name="description"
                    content="To streamline and effortlessly manage and send interview invitations to candidates, connecting with potential hires seamlessly, on OfferX to improve the hiring process."
                />
            </Helmet>

            <div className="company-page-contener">
                {showBulkUploadPage && (
                    <div className="my-3">
                        <span className="pe-2 text-secondary">
                            <span
                                className="cursor-pointer"
                                onClick={() => {
                                    setShowBulkUploadPage(false);
                                    setInvitationHeaderText('Candidate invitations');
                                    signOutServiceComponent();
                                }}
                            >
                                <i className="bi bi-chevron-left lt-text-secondary-alt me-0"></i> Back
                            </span>
                        </span>
                    </div>
                )}
                <div className="d-flex my-4 align-items-center">
                    <div className="flex-grow-1 text-start">
                        <h1 className="fw-700 fs-20 page-header-title mb-0 lt-text-secondary">
                            {invitationHeaderText}
                        </h1>
                    </div>
                    <div className="flex-shrink-1 text-sm-end">
                        <button
                            className={`btn btn-primary mx-2 ${showBulkUploadPage && 'd-none'}`}
                            type="button"
                            data-bs-toggle={!showVerificationWarning ? '' : 'modal'}
                            data-bs-target={!showVerificationWarning ? '' : '#AddInvitePage'}
                            onClick={() => {
                                showWarning(hasUser, setShowVerificationWarning);
                                signOutServiceComponent();
                            }}
                        >
                            Invite a new candidate
                        </button>
                        {isExitReviewEnabled && (
                            <>
                                <button
                                    className={`btn btn-outline-primary outline-p-hover mx-2 ${
                                        showBulkUploadPage && 'd-none'
                                    }`}
                                    type="button"
                                    onClick={() => {
                                        showWarning(hasUser, setShowVerificationWarning);
                                        setInvitationHeaderText('Invite new candidates in bulk');
                                        signOutServiceComponent();
                                        const result = showWarning(hasUser, setShowVerificationWarning);

                                        if (showVerificationWarning && result) {
                                            setShowBulkUploadPage(true);
                                            setIsExistingCandidate(false);
                                        } else {
                                            setShowBulkUploadPage(false);
                                            setIsExistingCandidate(true);
                                        }
                                    }}
                                >
                                    Invite new candidates in bulk
                                </button>
                                <button
                                    className={`btn btn-outline-primary outline-p-hover mx-2 ${
                                        showBulkUploadPage && 'd-none'
                                    }`}
                                    type="button"
                                    onClick={() => {
                                        showWarning(hasUser, setShowVerificationWarning);
                                        setInvitationHeaderText('Invite existing employees in bulk');
                                        signOutServiceComponent();
                                        const result = showWarning(hasUser, setShowVerificationWarning);

                                        if (showVerificationWarning && result) {
                                            setShowBulkUploadPage(true);
                                            setIsExistingCandidate(true);
                                        } else {
                                            setShowBulkUploadPage(false);
                                            setIsExistingCandidate(false);
                                        }
                                    }}
                                >
                                    Invite existing employees in bulk
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <PageLoader />
                ) : (
                    <>
                        <div className="card lt-invitation-table">
                            {!showBulkUploadPage ? (
                                <Table columns={columns} data={data} />
                            ) : (
                                <CandidateExcelBulkUpload
                                    setShowCandidateUploadContent={setShowBulkUploadPage}
                                    employerCompanyDomain={employerCompanyDomainValue}
                                    isExistingCandidate={isExistingCandidate}
                                />
                            )}
                        </div>
                    </>
                )}

                <CreateInvite id="AddInvitePage" getList={getList} />
            </div>
        </>
    );
};

export default CandidateInvitations;

const getStatusColor = (value: InviteStatusEnum) => {
    switch (value) {
        case InviteStatusEnum.invited:
            return '#AE8701';
        case InviteStatusEnum.joined:
            return '#107C10';
        case InviteStatusEnum.deleted:
            return '#D83B01';
        default:
            return '';
    }
};

const getInviteTypeColor = (value: InviteTypeEnum) => {
    switch (value) {
        case InviteTypeEnum.newCandidate:
            return '#107C10';
        case InviteTypeEnum.existingEmployee:
            return '#AE8701';
        case InviteTypeEnum.external:
            // return '#AE8701';
            return '#107C10';
        default:
            return '';
    }
};
